import { toast } from "@devhop/ui";
import type { QueryClient } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { competitionApi } from "./client";

export const competitionKeys = {
  all: ["competition"] as const,
  divisions: (eventId: string) =>
    ["competition", "divisions", eventId] as const,
  teamsRoot: (eventId: string) => ["competition", "teams", eventId] as const,
  teams: (
    eventId: string,
    divisionId?: string,
    roundIndex?: number,
  ): readonly unknown[] =>
    [
      ...competitionKeys.teamsRoot(eventId),
      divisionId && divisionId.length > 0 ? divisionId : "all",
      typeof roundIndex === "number" && roundIndex >= 1 ? roundIndex : "any",
    ] as const,
  settings: (eventId: string) => ["competition", "settings", eventId] as const,
  results: (eventId: string) => ["competition", "results", eventId] as const,
  publishedCatalog: ["competition", "published-catalog"] as const,
};

export function invalidateTeamsQueries(qc: QueryClient, eventId: string) {
  void qc.invalidateQueries({
    queryKey: competitionKeys.teamsRoot(eventId),
  });
}

export function useDivisionsQuery(eventId: string | undefined) {
  return useQuery({
    queryKey: competitionKeys.divisions(eventId ?? ""),
    queryFn: () => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.listDivisions(eventId);
    },
    enabled: Boolean(eventId),
  });
}

export function useCreateDivision(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      slug?: string | null;
      sortOrder?: number;
    }) => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.createDivision(eventId, body);
    },
    onSuccess: () => {
      toast.success("ເພີ່ມຮາງສຳເລັດ");
      if (eventId) {
        qc.invalidateQueries({ queryKey: competitionKeys.divisions(eventId) });
        invalidateTeamsQueries(qc, eventId);
      }
    },
    onError: () => toast.error("ເພີ່ມຮາງລົ້ມເຫຼວ"),
  });
}

export function useDeleteDivision(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (divisionId: string) => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.deleteDivision(eventId, divisionId);
    },
    onSuccess: () => {
      toast.success("ລຶບຮາງສຳເລັດ");
      if (eventId) {
        qc.invalidateQueries({ queryKey: competitionKeys.divisions(eventId) });
        invalidateTeamsQueries(qc, eventId);
        qc.invalidateQueries({ queryKey: competitionKeys.results(eventId) });
      }
    },
    onError: () => toast.error("ລຶບຮາງລົ້ມເຫຼວ"),
  });
}

export function usePatchDivisionRoundConfig(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      divisionId,
      body,
    }: {
      divisionId: string;
      body: {
        roundCount: number;
        qualifiers: { fromRound: number; minScore: number }[];
      };
    }) => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.patchDivisionRoundConfig(eventId, divisionId, body);
    },
    onSuccess: () => {
      toast.success("ບັນທຶກຮູບແບບຮອບແລ້ວ");
      if (eventId) {
        qc.invalidateQueries({ queryKey: competitionKeys.divisions(eventId) });
        invalidateTeamsQueries(qc, eventId);
        qc.invalidateQueries({ queryKey: competitionKeys.results(eventId) });
      }
    },
    onError: () => toast.error("ບັນທຶກລົ້ມເຫຼວ"),
  });
}

export function useCreateDivisionCriterion(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      divisionId,
      body,
    }: {
      divisionId: string;
      body: {
        judgeRole: "judge1" | "judge2" | "judge3";
        name: string;
        maxScore: number;
        sortOrder?: number;
      };
    }) => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.createDivisionCriterion(eventId, divisionId, body);
    },
    onSuccess: () => {
      toast.success("ເພີ່ມ criteria ສຳເລັດ");
      if (eventId) {
        qc.invalidateQueries({ queryKey: competitionKeys.divisions(eventId) });
        qc.invalidateQueries({ queryKey: competitionKeys.results(eventId) });
      }
    },
    onError: () => toast.error("ເພີ່ມ criteria ລົ້ມເຫຼວ"),
  });
}

export function useDeleteDivisionCriterion(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      divisionId,
      criterionId,
    }: {
      divisionId: string;
      criterionId: string;
    }) => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.deleteDivisionCriterion(
        eventId,
        divisionId,
        criterionId,
      );
    },
    onSuccess: () => {
      toast.success("ລຶບ criteria ສຳເລັດ");
      if (eventId) {
        qc.invalidateQueries({ queryKey: competitionKeys.divisions(eventId) });
        qc.invalidateQueries({ queryKey: competitionKeys.results(eventId) });
      }
    },
    onError: () => toast.error("ລຶບ criteria ລົ້ມເຫຼວ"),
  });
}

export function useCloseDivisionRound(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      divisionId,
      finishedRound,
    }: {
      divisionId: string;
      finishedRound: number;
    }) => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.closeDivisionRound(
        eventId,
        divisionId,
        finishedRound,
      );
    },
    onSuccess: (data) => {
      toast.success(`ຜ່ານເຂົ້າຮອບແລ້ວ (${data.advancedCount} ທີມ)`);
      if (eventId) {
        qc.invalidateQueries({ queryKey: competitionKeys.divisions(eventId) });
        invalidateTeamsQueries(qc, eventId);
        qc.invalidateQueries({ queryKey: competitionKeys.results(eventId) });
      }
    },
    onError: () => toast.error("ປິດຮອບລົ້ມເຫຼວ"),
  });
}

export function useTeamsQuery(
  eventId: string | undefined,
  divisionFilter?: string,
  options?: { enabled?: boolean; roundIndex?: number },
) {
  const extraEnabled = options?.enabled ?? true;
  const roundIx = options?.roundIndex;
  return useQuery({
    queryKey: competitionKeys.teams(eventId ?? "", divisionFilter, roundIx),
    queryFn: () => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.listTeams(eventId, divisionFilter, roundIx);
    },
    enabled: Boolean(eventId) && extraEnabled,
  });
}

export function useCreateTeam(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      divisionId: string;
      name: string;
      orderNumber: number;
    }) => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.createTeam(eventId, input);
    },
    onSuccess: () => {
      toast.success("ເພີ່ມທີມສຳເລັດ");
      if (eventId) invalidateTeamsQueries(qc, eventId);
    },
    onError: () => toast.error("ເພີ່ມທີມລົ້ມເຫຼວ"),
  });
}

export function useUpdateTeam(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      divisionId?: string;
      name?: string;
      orderNumber?: number;
    }) => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.updateTeam(eventId, id, input);
    },
    onSuccess: () => {
      toast.success("ແກ້ໄຂທີມສຳເລັດ");
      if (eventId) {
        invalidateTeamsQueries(qc, eventId);
        qc.invalidateQueries({ queryKey: competitionKeys.results(eventId) });
      }
    },
    onError: () => toast.error("ແກ້ໄຂທີມລົ້ມເຫຼວ"),
  });
}

export function useDeleteTeam(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.deleteTeam(eventId, id);
    },
    onSuccess: () => {
      toast.success("ລຶບທີມສຳເລັດ");
      if (eventId) {
        invalidateTeamsQueries(qc, eventId);
        qc.invalidateQueries({ queryKey: competitionKeys.results(eventId) });
      }
    },
    onError: () => toast.error("ລຶບທີມລົ້ມເຫຼວ"),
  });
}

export function useSettingsQuery(eventId: string | undefined) {
  return useQuery({
    queryKey: competitionKeys.settings(eventId ?? ""),
    queryFn: () => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.getSettings(eventId);
    },
    refetchInterval: 10_000,
    enabled: Boolean(eventId),
  });
}

export function useToggleScoring(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.toggleScoring(eventId);
    },
    onSuccess: (data) => {
      toast.success(data.isOpen ? "ເປີດການໃຫ້ຄະແນນແລ້ວ" : "ປິດການໃຫ້ຄະແນນແລ້ວ");
      if (eventId)
        qc.invalidateQueries({ queryKey: competitionKeys.settings(eventId) });
    },
    onError: () => toast.error("ດຳເນີນການລົ້ມເຫຼວ"),
  });
}

export function usePublishResults(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.publishResults(eventId);
    },
    onSuccess: () => {
      toast.success("ປະກາດຜົນສຳເລັດ");
      if (eventId) {
        qc.invalidateQueries({ queryKey: competitionKeys.settings(eventId) });
        qc.invalidateQueries({ queryKey: competitionKeys.results(eventId) });
      }
    },
    onError: () => toast.error("ປະກາດຜົນລົ້ມເຫຼວ"),
  });
}

export function useReopenScoring(eventId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.reopenScoring(eventId);
    },
    onSuccess: () => {
      toast.success("ເປີດໃຫ້ຄະແນນຄືນແລ້ວ — ຜົນຖືກເຊື່ອງຈາກສາທາລະນະ");
      if (eventId) {
        qc.invalidateQueries({ queryKey: competitionKeys.settings(eventId) });
        qc.invalidateQueries({ queryKey: competitionKeys.results(eventId) });
      }
    },
    onError: () => toast.error("ເປີດໃຫ້ຄະແນນຄືນລົ້ມເຫຼວ"),
  });
}

export function useResultsQuery(eventId: string | undefined) {
  return useQuery({
    queryKey: competitionKeys.results(eventId ?? ""),
    queryFn: () => {
      if (!eventId) throw new Error("Missing event id");
      return competitionApi.getResults(eventId);
    },
    enabled: Boolean(eventId),
  });
}

export function usePublishedCatalogQuery() {
  return useQuery({
    queryKey: competitionKeys.publishedCatalog,
    queryFn: () => competitionApi.listPublishedEventCatalog(),
  });
}
